import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { noop } from 'lodash';
import SlickSlider, { Settings } from 'react-slick';
import { SliderPaginationButton } from './PaginationButton';
import { SliderPaginationDots } from './PaginationDots';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

export interface ISlider {
  className?: string;
  settings: Settings;
  responsiveTabletSettings?: Partial<Settings>;
  responsivePhabletSettings?: Partial<Settings>;
  responsivePhoneSettings?: Partial<Settings>;
  titles?: string[];
  children?: any;
  paginationMargin?: string;
  onChange?: (currentSlide: number) => void;
}

type StyledSliderProps = {
  paginationMargin: string;
};

const StyledSlider = styled.div<StyledSliderProps>`
  width: 100%;
  &:after {
    content: '';
    height: ${rem(`60px`)};
    width: 100%;
    display: block;
    ${({ theme }) => theme.breakpoints.phone`
        height: ${rem(`96px`)};
      `}
  }
  & .slick-track {
    display: flex;
    align-items: center;
    ${({ theme }) => theme.breakpoints.desktop`
      padding-top: ${rem(`24px`)};
    `}
  }
  & .slick-track .slick-slide {
    text-align: center;
  }
  .slick-dots {
    bottom: initial;
    top: 100%;
  }
  & .slick-slider {
    ${({ theme }) => theme.breakpoints.desktop`
      padding-bottom: ${({ paginationMargin }) => rem(paginationMargin)};
    `}
  }
  & .slick-slider .slick-list {
    margin: 0 -${rem(`6px`)};
    ${({ theme }) => theme.breakpoints.desktop`
      margin: 0 -${rem(`14px`)};
      padding: ${rem(`4px`)} 0;
    `}
  }
  & .slick-slide > div {
    height: 100%;
  }
`;

export const Slider = ({
  className,
  settings,
  titles,
  children,
  responsiveTabletSettings = {},
  responsivePhabletSettings = {},
  responsivePhoneSettings = {},
  paginationMargin = `0px`,
  onChange = noop,
}: ISlider) => {
  const [sliderRef, setSliderRef] = useState<SlickSlider>(undefined);
  const [nav, setNav] = useState(undefined);
  const [slide, setSlide] = useState(0);
  const [currentSettings, setCurrentSettings] = useState<Partial<Settings>>({});
  const childrenByRows = currentSettings?.rows
    ? children.length / currentSettings?.rows
    : children.length;
  const lastSlide = childrenByRows - currentSettings.slidesToShow;
  const currentSlideDelta =
    currentSettings.slidesToShow > 1
      ? slide + currentSettings.slidesToShow
      : slide;

  const mergedSettings: Settings = {
    slidesToShow: 4,
    slidesToScroll: 4,
    infinite: true,
    dots: true,
    fade: false,
    focusOnSelect: false,
    arrows: true,
    autoplay: false,
    speed: 300,
    autoplaySpeed: 2500,
    cssEase: `ease-in-out`,
    pauseOnHover: true,
    swipeToSlide: true,
    centerPadding: `0px`,
    slidesPerRow: 1,
    nextArrow: (
      <SliderPaginationButton
        direction="next"
        slickRef={sliderRef}
        currentSlide={currentSlideDelta}
        lastSlide={lastSlide}
      />
    ),
    prevArrow: (
      <SliderPaginationButton
        direction="previous"
        slickRef={sliderRef}
        lastSlide={lastSlide}
        currentSlide={currentSlideDelta}
      />
    ),
    appendDots: (dots) => (
      <div>
        <SliderPaginationDots
          dots={dots}
          titles={titles}
          currentSlide={slide}
        />
      </div>
    ),
    afterChange: (currentSlide: number) => {
      setSlide(currentSlide);
      onChange(currentSlide);
    },
    responsive: [
      {
        breakpoint: 1366,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          ...responsiveTabletSettings,
        },
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          appendDots: (dots) => (
            <div>
              <SliderPaginationDots
                dots={dots}
                titles={titles}
                currentSlide={
                  currentSettings.slidesToScroll > 1
                    ? slide / currentSettings.slidesToScroll
                    : slide
                }
                asNumbers
              />
            </div>
          ),
          ...responsivePhabletSettings,
        },
      },
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          appendDots: (dots) => (
            <div>
              <SliderPaginationDots
                dots={dots}
                asNumbers
                titles={titles}
                currentSlide={slide}
              />
            </div>
          ),
          ...responsivePhoneSettings,
        },
      },
    ],
    ...settings,
  };

  useEffect(() => {
    setNav(sliderRef);
    return () => {
      setNav(undefined);
    };
  });

  useEffect(() => {
    if (!sliderRef) {
      return;
    }
    const sRef = sliderRef as any;
    const currentBreakpoint = sRef.state.breakpoint;
    const resposniveRefSettings = sRef.props.responsive.find(
      ({ breakpoint }) => breakpoint === currentBreakpoint,
    )?.settings;
    const { slidesPerRow, slidesToScroll, slidesToShow, rows } = sRef.props;
    const newSettings: Partial<Settings> = {
      slidesPerRow,
      slidesToScroll,
      slidesToShow,
      rows,
      ...resposniveRefSettings,
    };
    setCurrentSettings(newSettings);
  }, [sliderRef, slide]);

  return (
    <StyledSlider paginationMargin={paginationMargin} className={className}>
      <SlickSlider
        {...mergedSettings}
        asNavFor={nav}
        ref={(sliderInstance) => setSliderRef(sliderInstance)}
      >
        {children}
      </SlickSlider>
    </StyledSlider>
  );
};

const StyledSliderButton = styled.button`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  padding: 0 ${rem(`6px`)};
  ${({ theme }) => theme.breakpoints.desktop`
      padding: 0 ${rem(`14px`)};
    `}
`;

Slider.Button = ({
  children,
  onClick,
  className,
}: {
  children: any;
  onClick: () => void;
  className?: string;
}) => (
  <StyledSliderButton className={className} type="button" onClick={onClick}>
    {children}
  </StyledSliderButton>
);
